import React from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendIcon from "@mui/icons-material/Send";
import PageSection from "../PageSection";
import NoticeCard from "../NoticeCard";
import RescheduleDialog from "../scheduling/RescheduleDialog";
import { Subcircle } from "../../hooks/useSubcircles";
import { lightTheme } from "../../styles/theme";
import { isStaffOnlyNotice } from "../../utils/noticeScheduling";
import type { NoticeWithType } from ".";

interface ScheduledNoticesListProps {
  notices: NoticeWithType[];
  actionNoticeId?: string | null;
  rescheduleTarget: NoticeWithType | null;
  savingReschedule?: boolean;
  onNoticeClick: (notice: NoticeWithType) => void;
  onReschedule: (notice: NoticeWithType) => void;
  onRescheduleClose: () => void;
  onRescheduleSave: (notice: NoticeWithType, publishAt: string) => void;
  onCancel: (notice: NoticeWithType) => void;
  onPublishNow: (notice: NoticeWithType) => void;
  getReadableSenderName: (sender: string | undefined) => string;
  schoolName: string;
  subcircles: Subcircle[];
  orgMemberCount?: number;
}

const actionTileSx = (filled = false) => ({
  width: 76,
  height: 76,
  flexShrink: 0,
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  p: 0.75,
  border: "1px solid",
  borderColor: "primary.main",
  borderRadius: 2,
  bgcolor: filled ? "primary.main" : "background.paper",
  color: filled ? "common.white" : "primary.main",
  cursor: "pointer",
  transition:
    "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease",
  "&:hover:not(:disabled)": {
    bgcolor: "primary.main",
    borderColor: "primary.main",
    color: "common.white",
  },
  "&:disabled": {
    opacity: 0.55,
    cursor: "not-allowed",
  },
});

const ScheduledNoticesList: React.FC<ScheduledNoticesListProps> = ({
  notices,
  actionNoticeId = null,
  rescheduleTarget,
  savingReschedule = false,
  onNoticeClick,
  onReschedule,
  onRescheduleClose,
  onRescheduleSave,
  onCancel,
  onPublishNow,
  getReadableSenderName,
  schoolName,
  subcircles,
}) => {
  const theme = useTheme();

  if (notices.length === 0) {
    return (
      <PageSection>
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography color="text.secondary" variant="body1">
            No scheduled notices match your search.
          </Typography>
        </Box>
      </PageSection>
    );
  }

  return (
    <PageSection>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {notices.map((notice) => {
          const noticeId = notice.noticeId || notice.id;
          const isBusy = actionNoticeId === noticeId;

          return (
            <Paper
              key={noticeId}
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.25 },
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                boxShadow: "none",
                cursor: "pointer",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                display: "flex",
                alignItems: "stretch",
                gap: 0.75,
                overflow: "hidden",
                bgcolor: "background.paper",
                "&:hover": {
                  borderColor: "primary.main",
                  boxShadow: `0 2px 10px ${theme.palette.shadows.dark}`,
                },
              }}
              onClick={() => onNoticeClick(notice)}
            >
              <Box sx={{ flex: 1, minWidth: 0, py: 0.25 }}>
                <NoticeCard
                  variant="list"
                  title={notice.title}
                  body={notice.body}
                  createdAt={notice.createdAt}
                  sender={getReadableSenderName(notice.sender)}
                  type={notice.type}
                  isUrgent={notice.isUrgent}
                  publishStatus={notice.publishStatus}
                  publishAt={notice.publishAt}
                  staffOnly={notice.staffOnly}
                  targetSubcircle={notice.targetSubcircle}
                  attachments={notice.attachments}
                  schoolName={schoolName}
                  subcircles={subcircles}
                />
              </Box>

              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  pl: { xs: 0, md: 1 },
                  pt: { xs: 1.5, md: 0 },
                  borderTop: {
                    xs: `1px solid ${lightTheme.colors.borderSlateLight}`,
                    md: "none",
                  },
                  borderLeft: {
                    md: `1px solid ${lightTheme.colors.borderSlateLight}`,
                  },
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <Box
                    component="button"
                    type="button"
                    aria-label="Edit schedule"
                    disabled={isBusy}
                    onClick={() => onReschedule(notice)}
                    sx={actionTileSx(false)}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 24 }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.625rem",
                        fontWeight: 600,
                        lineHeight: 1.15,
                        textAlign: "center",
                      }}
                    >
                      Edit
                    </Typography>
                  </Box>
                  {!isStaffOnlyNotice(notice) && (
                    <Box
                      component="button"
                      type="button"
                      aria-label="Publish now"
                      disabled={isBusy}
                      onClick={() => void onPublishNow(notice)}
                      sx={actionTileSx(true)}
                    >
                      {isBusy ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        <SendIcon sx={{ fontSize: 24 }} />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.625rem",
                          fontWeight: 600,
                          lineHeight: 1.15,
                          textAlign: "center",
                        }}
                      >
                        Publish now
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography
                  component="button"
                  type="button"
                  variant="caption"
                  disabled={isBusy}
                  onClick={() => void onCancel(notice)}
                  sx={{
                    border: "none",
                    bgcolor: "transparent",
                    p: 0,
                    color: "error.main",
                    fontWeight: 500,
                    fontSize: "0.7rem",
                    cursor: isBusy ? "not-allowed" : "pointer",
                    opacity: isBusy ? 0.55 : 1,
                    textDecoration: "none",
                    "&:hover:not(:disabled)": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Cancel schedule
                </Typography>
              </Box>
            </Paper>
          );
        })}
      </Box>

      <RescheduleDialog
        open={rescheduleTarget !== null}
        notice={rescheduleTarget}
        saving={savingReschedule}
        onClose={onRescheduleClose}
        onSave={(notice, publishAt) =>
          onRescheduleSave(notice as NoticeWithType, publishAt)
        }
      />
    </PageSection>
  );
};

export default ScheduledNoticesList;
